import { useEffect, useState, useCallback } from "react";
import { getMe } from "@/lib/api";

/**
 * useMe — calls /api/me and honors window.JH_PAID. Preview-only ?demo=1 flag
 * lets you click through the full UI without a real session — guarded so it
 * cannot activate on jobhacki.com (production).
 *
 *   ?demo=1     → logged-in, ALL-ACCESS
 *   ?demo=free  → logged-in, FREE tier (to test paid-gated routes)
 *   ?demo=out   → logged out (forces LoginPanel state)
 */
function readDemoMode() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  const isProd =
    host === "jobhacki.com" || host.endsWith(".jobhacki.com");
  if (isProd) return null;
  const q = new URLSearchParams(window.location.search);
  const v = q.get("demo");
  if (!v) return null;
  if (v === "out") return { ok: false, paid: false };
  if (v === "free") {
    return {
      ok: true,
      paid: false,
      user: { email: "demo@jobhacki.com" },
      demo: true,
    };
  }
  return {
    ok: true,
    paid: true,
    user: { email: "demo@jobhacki.com" },
    demo: true,
  };
}

export function useMe() {
  const [me, setMe] = useState(undefined);

  const refresh = useCallback(async () => {
    const demo = readDemoMode();
    if (demo) {
      setMe(demo);
      return;
    }
    try {
      const data = await getMe();
      const paidFromWindow =
        typeof window !== "undefined" && window.JH_PAID === true;
      setMe({
        ...data,
        ok: data?.ok === true,
        paid: Boolean(
          data?.paid || data?.entitlement === "all_access" || paidFromWindow
        ),
      });
    } catch {
      setMe({ ok: false, paid: false, error: "network" });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { me, refresh };
}
